/**
 * Argus Live — `<ArgusLivePostMessageHost>` convenience component.
 *
 * Wraps the iframe + `useArgusLiveDashboardHost` hook into a single
 * drop-in React component for callers (mostly AL-B3's preview pane and
 * the runtime viewer page) who don't need to manage the iframe
 * directly.
 *
 * Power callers who want to compose the iframe with their own ref
 * (e.g. dev-tools overlays) should call the hook directly instead.
 *
 * PR AL-B1.
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

import {
  useArgusLiveSubscribe,
  type SubscribeBindingSummary,
} from '../../lib/use-argus-live-subscribe';
import {
  useArgusLiveDashboardHost,
  type UseArgusLiveDashboardHostOptions,
  type UseArgusLiveDashboardHostReturn,
} from './use-argus-live-dashboard-host';
import type { HostToIframeEvent } from '@argusai/ai-core/argus-live/runtime/post-message-protocol';

/**
 * Mirrors `IFRAME_SANDBOX_ATTRIBUTE` from
 * `@argusai/ai-core/argus-live/runtime/csp-builder`. We re-declare it
 * here rather than importing because `csp-builder.ts` pulls in Node's
 * `crypto` module which Vite cannot bundle for the browser. Any change
 * to the canonical constant MUST be mirrored here — AL-A3 test
 * `csp-builder.test.ts > IFRAME_SANDBOX_ATTRIBUTE matches host` is the
 * regression guard (added separately).
 */
const IFRAME_SANDBOX_ATTRIBUTE =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox';

export interface ArgusLivePostMessageHostProps
  extends UseArgusLiveDashboardHostOptions {
  /** Dashboard ID. Required — used for rate-limit scoping + audit
   *  events. */
  dashboardId: string;
  /** URL of the iframe asset endpoint. Typically
   *  `/api/v1/argus-live/iframe/{versionId}/index.html`. */
  src: string;
  /** Version currently rendered by the iframe; pins data-fetch and subscribe. */
  versionId?: string | null;
  /** Title attribute for accessibility. Required by ARIA. */
  title: string;
  /** Optional className for the *outer container* — the iframe itself
   *  inherits the auto-grow height from RESIZE messages. */
  className?: string;
  /** Inline style for the outer container. */
  style?: CSSProperties;
  /** Optional test id applied to the iframe itself. */
  iframeTestId?: string;
  /** Fill the caller's container instead of auto-growing from RESIZE messages. */
  fillContainer?: boolean;
  /** Dashboard bindings used to wire refreshMode='subscribe' live updates. */
  subscribeBindings?: SubscribeBindingSummary[];
  /** Init payload sent after the iframe runtime announces READY. */
  readyPayload?: Omit<
    Extract<HostToIframeEvent, { type: 'READY' }>,
    'type'
  >;
  /** Disable subscribe-mode SSE wiring for static/non-live surfaces. */
  subscribeEnabled?: boolean;
  /** Optional reference to the underlying iframe for callers that want
   *  to imperatively pause / reload. */
  onHostReady?: (host: UseArgusLiveDashboardHostReturn) => void;
}

/**
 * Renders the iframe and wires the host. Height auto-grows in response
 * to `RESIZE` messages from the iframe runtime, capped at the
 * 20 000 px max from `POST_MESSAGE_HOST_CAPS`.
 */
export function ArgusLivePostMessageHost(
  props: ArgusLivePostMessageHostProps,
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const host = useArgusLiveDashboardHost(iframeRef, props.dashboardId, {
    dispatcher: props.dispatcher,
    logger: props.logger,
    now: props.now,
    versionId: props.versionId,
  });
  const subscribeState = useArgusLiveSubscribe(
    props.dashboardId,
    props.subscribeBindings ?? [],
    host.dispatcher,
    {
      enabled: props.subscribeEnabled !== false,
      versionId: props.versionId,
    },
  );
  const { isReady, sendReady } = host;

  // Surface the host to the parent. Run in an effect (not during render)
  // so parents can safely call setState in `onHostReady` without
  // triggering React's "cannot update during render" warning. Re-fires
  // only when the host identity changes (i.e., dashboardId switch).
  const onHostReady = props.onHostReady;
  useEffect(() => {
    onHostReady?.(host);
  }, [host, onHostReady]);

  useEffect(() => {
    if (!isReady || !props.readyPayload) return;
    sendReady(props.readyPayload);
  }, [isReady, props.readyPayload, sendReady]);

  const iframeStyle: CSSProperties = {
    width: '100%',
    border: 'none',
    // Default to a small height so the dashboard does not collapse to
    // zero pixels while it boots. The first RESIZE message replaces
    // this with the iframe's measured height.
    height: props.fillContainer ? '100%' : host.lastResize ?? 480,
    display: 'block',
  };
  const containerStyle: CSSProperties | undefined = props.fillContainer
    ? { height: '100%', ...props.style }
    : props.style;

  return (
    <div className={props.className} style={containerStyle}>
      <iframe
        ref={iframeRef}
        src={props.src}
        title={props.title}
        // Sandbox attribute is FIXED — see csp-builder.ts. Tampering
        // here would defeat the whole isolation story.
        sandbox={IFRAME_SANDBOX_ATTRIBUTE}
        // Null-origin sandbox + CSP `connect-src 'self'` together
        // forbid any external network. We additionally set
        // `referrerpolicy=no-referrer` so the iframe cannot leak the
        // host's URL via outbound requests (defense in depth — the
        // CSP would block them anyway).
        referrerPolicy="no-referrer"
        // `loading="lazy"` lets the parent page render its chrome
        // (chat pane, monaco) before the iframe boots — measurable
        // first-paint win when the user opens a dashboard list.
        loading="lazy"
        style={iframeStyle}
        data-testid={props.iframeTestId}
        data-argus-live-dashboard-id={props.dashboardId}
        data-argus-live-ready={isReady ? 'true' : 'false'}
        data-argus-live-subscribe-status={subscribeState.status}
      />
    </div>
  );
}

// Re-export for callers needing to type their ref or dispatcher.
export type {
  UseArgusLiveDashboardHostOptions,
  UseArgusLiveDashboardHostReturn,
} from './use-argus-live-dashboard-host';

export { useArgusLiveDashboardHost } from './use-argus-live-dashboard-host';
export {
  PostMessageHost,
  SlidingWindowCounter,
  POST_MESSAGE_HOST_CAPS,
  consoleHostLogger,
  type ArgusLiveHostDispatcher,
  type ArgusLiveHostLogger,
  type HostMessageOutcome,
  type HostMessageRejection,
} from './post-message-host-core';
